/**
 * The list manager's model: what the screen shows (kept in the URL, so the way
 * back from a list's people restores it), the API variables for a page, and
 * the editor's checks. Pure, so it is tested without rendering.
 */

export interface ManagedList {
  id: number;
  name: string;
  description: string | null;
  /** STATIC: a list, people added by hand or from a filter. DYNAMIC: a saved filter. */
  type: 'STATIC' | 'DYNAMIC';
  source: string;
  /** False for a saved filter migrated from NationBuilder and not yet re-expressed. */
  isActive: boolean;
  /** A list's members, exact; a saved filter's matches when it was last counted. */
  contactCount: number;
  countedAt: string | null;
  nbCreatedAt: string | null;
  nbSource: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type ListKind = 'all' | 'STATIC' | 'DYNAMIC';

export interface ListView {
  search: string;
  kind: ListKind;
  /** From 0. */
  page: number;
  pageSize: number;
}

export const PAGE_SIZES: ReadonlyArray<number> = [25, 50, 100];

/** The API's limit on a search. */
export const SEARCH_LIMIT = 100;

export const DEFAULT_LIST_VIEW: ListView = { search: '', kind: 'all', page: 0, pageSize: PAGE_SIZES[0] };

const KINDS: ReadonlyArray<ListKind> = ['all', 'STATIC', 'DYNAMIC'];

const URL_KIND: Record<ListKind, string> = { all: '', STATIC: 'lists', DYNAMIC: 'filters' };

/** Reads the view from the URL. Pages are counted from 1 there; anything malformed falls back. */
export const parseListView = (params: URLSearchParams): ListView => {
  const kind = KINDS.find((k) => URL_KIND[k] === params.get('kind')) ?? 'all';
  const page = Number(params.get('page'));
  const size = Number(params.get('size'));
  return {
    search: (params.get('q') ?? '').slice(0, SEARCH_LIMIT),
    kind,
    page: Number.isInteger(page) && page > 1 ? page - 1 : 0,
    pageSize: PAGE_SIZES.includes(size) ? size : PAGE_SIZES[0],
  };
};

/** The URL form of a view, leaving out every default. */
export const listViewParams = (view: ListView): URLSearchParams => {
  const params = new URLSearchParams();
  if (view.search) params.set('q', view.search);
  if (view.kind !== 'all') params.set('kind', URL_KIND[view.kind]);
  if (view.page > 0) params.set('page', String(view.page + 1));
  if (view.pageSize !== PAGE_SIZES[0]) params.set('size', String(view.pageSize));
  return params;
};

/** crmSegments' variables for a view. An empty search is left out, not sent as "". */
export const listVariables = (view: ListView) => ({
  ...(view.search.trim() ? { search: view.search.trim() } : {}),
  ...(view.kind !== 'all' ? { type: view.kind } : {}),
  skip: view.page * view.pageSize,
  take: view.pageSize,
});

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

export const kindLabel = (list: Pick<ManagedList, 'type'>) => (list.type === 'STATIC' ? 'List' : 'Saved filter');

const ORIGINS: Record<string, string> = {
  NATIONBUILDER: 'NationBuilder',
  MANUAL: 'Made here',
  IMPORT: 'An import',
  APP: 'The app',
  WORKFLOW: 'A workflow',
};

export const originLabel = (list: Pick<ManagedList, 'source'>) => ORIGINS[list.source] ?? list.source;

/** When it was made: NationBuilder's own date for a migrated list, else ours. */
export const madeAt = (list: Pick<ManagedList, 'nbCreatedAt' | 'createdAt'>) => list.nbCreatedAt ?? list.createdAt;

/* ------------------------------------------------------------------ *
 * The editor
 * ------------------------------------------------------------------ */

/** The API's limits (crm-lists.ts in ilbp-backend). */
export const LIST_LIMITS = { name: 100, description: 500 } as const;

export interface ListForm {
  name: string;
  description: string;
}

export const EMPTY_LIST_FORM: ListForm = { name: '', description: '' };

export const listFormOf = (list: ManagedList): ListForm => ({ name: list.name, description: list.description ?? '' });

const clean = (value: string) => value.trim().replace(/\s+/g, ' ');

export const checkListForm = (form: ListForm): Partial<Record<keyof ListForm, string>> => {
  const problems: Partial<Record<keyof ListForm, string>> = {};
  const name = clean(form.name);
  if (!name) problems.name = 'A list needs a name.';
  else if (name.length > LIST_LIMITS.name) problems.name = `At most ${LIST_LIMITS.name} characters.`;
  if (form.description.trim().length > LIST_LIMITS.description) {
    problems.description = `At most ${LIST_LIMITS.description} characters.`;
  }
  return problems;
};

export const listCreateVariables = (form: ListForm) => ({
  name: clean(form.name),
  ...(form.description.trim() ? { description: form.description.trim() } : {}),
});

/** Only what changed; an emptied description is sent as "", which clears it. */
export const listUpdateVariables = (list: ManagedList, form: ListForm) => {
  const changes: { name?: string; description?: string } = {};
  if (clean(form.name) !== list.name) changes.name = clean(form.name);
  if (form.description.trim() !== (list.description ?? '')) changes.description = form.description.trim();
  return changes;
};
